import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch, useSelector } from "src/store";

import { showDetails } from "@devices/store";
import { selectDevicesByGroupId } from "@devices/store/selectors/devices";
import { Group } from "@devices/types";
import UserSettingsService from "@services/userSettingsService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { TextLink } from "@shared/TextLink";
import { useCurrentWorkspace } from "@workspaces/api/helpers";

type Props = {
    group: Group;
    isUnassigning: boolean;
    disabled: boolean;
    onUnassign: (groupId: number) => void;
};

export const AssignedGroupRow = ({ group, isUnassigning, disabled, onUnassign }: Props) => {
    const { t } = useTranslation(["pages", "common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { groupAbility } = PermissionService();
    const { data: workspace = null } = useCurrentWorkspace();

    const cannotUnassignPolicy = groupAbility(group, workspace).cannot("unassign", "Policy");

    const devices = useSelector((state) => selectDevicesByGroupId(state, group.id));

    // A saved filter or search on the groups page could hide the row of the group being opened.
    const showGroup = () => {
        UserSettingsService.setWorkspaceValue("groupPage.columnFilters", []);
        UserSettingsService.setWorkspaceValue("groupPage.search", "");
        dispatch(showDetails({ type: "group", selectedId: group.id }));
        void navigate("/dashboard/groups");
    };

    return (
        <HoverRow clickable={false}>
            <MinimalTableCell align="left">
                <TextLink onClick={showGroup} tooltip={t("common:tooltip.showGroupDetails")}>
                    {group.name}
                </TextLink>
            </MinimalTableCell>
            <MinimalTableCell align="left">{devices.length}</MinimalTableCell>
            <MinimalTableCell align="right">
                <DangerOutlinedButton
                    onClick={() => onUnassign(group.id)}
                    loading={isUnassigning}
                    disabled={disabled || cannotUnassignPolicy}
                    tooltipProps={{
                        title: cannotUnassignPolicy
                            ? t("common:tooltip.cannotManagePolicies")
                            : t("pages:policiesPage.tooltip.unassignGroup"),
                    }}
                >
                    <i className="fa-solid fa-fw fa-trash" />
                </DangerOutlinedButton>
            </MinimalTableCell>
        </HoverRow>
    );
};
